package com.sddamico.draftmachine.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.api.ApiException
import com.sddamico.draftmachine.components.layouts.LoadingFrame
import com.sddamico.draftmachine.components.layouts.MobileFrame
import com.sddamico.draftmachine.components.sections.AppHeader
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.displayBlock
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.dmStamp
import com.sddamico.draftmachine.ds.roleBody
import com.sddamico.draftmachine.ds.roleH2
import com.sddamico.draftmachine.ds.roleMono
import com.sddamico.draftmachine.ds.core.Card
import com.sddamico.draftmachine.ds.core.CardVariant
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.forms.Input
import com.sddamico.draftmachine.ds.feedback.Toast
import com.sddamico.draftmachine.ds.feedback.ToastTone
import com.sddamico.draftmachine.models.MeResponse
import com.sddamico.draftmachine.util.LoadState
import com.sddamico.draftmachine.util.UNREACHABLE_MESSAGE
import com.sddamico.draftmachine.util.rememberAuthState
import com.sddamico.draftmachine.util.transitionTo
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

@Page
@Composable
fun SettingsPage() {
  val ctx = rememberPageContext()
  val scope = rememberCoroutineScope()
  val auth = rememberAuthState()

  when (val state = auth.value) {
    is LoadState.Loading -> LoadingFrame()
    is LoadState.Failed -> {
      LaunchedEffect(Unit) { ctx.router.transitionTo("/login") }
      LoadingFrame()
    }
    is LoadState.Ready -> {
      val user = state.value
      if (user == null) {
        LaunchedEffect(Unit) { ctx.router.transitionTo("/login") }
        LoadingFrame()
      } else {
        AccountSettings(
          user = user,
          onBack = { ctx.router.transitionTo("/") },
          onVerifyAlerts = { ctx.router.transitionTo("/settings/alerts") },
          onUpdateUsername = auth::updateUsername,
          onLogout = {
            scope.launch {
              auth.logout()
              ctx.router.transitionTo("/login")
            }
          },
        )
      }
    }
  }
}

@Composable
private fun AccountSettings(
  user: MeResponse,
  onBack: () -> Unit,
  onVerifyAlerts: () -> Unit,
  onUpdateUsername: suspend (String) -> MeResponse,
  onLogout: () -> Unit,
) {
  val scope = rememberCoroutineScope()
  var username by remember(user.id) { mutableStateOf(user.username) }
  var usernameError by remember { mutableStateOf<String?>(null) }
  var saving by remember { mutableStateOf(false) }
  var saved by remember { mutableStateOf(false) }

  LaunchedEffect(user.username) { username = user.username }

  fun saveUsername() {
    if (saving || username.trim().lowercase() == user.username) return
    saving = true
    saved = false
    usernameError = null
    scope.launch {
      try {
        val updated = onUpdateUsername(username)
        username = updated.username
        saved = true
      } catch (e: ApiException) {
        usernameError = e.errorMessage
      } catch (_: Throwable) {
        usernameError = UNREACHABLE_MESSAGE
      } finally {
        saving = false
      }
    }
  }

  MobileFrame(
    modifier = Modifier.displayFlex().flexDirection(FlexDirection.Column),
    header = { AppHeader(email = user.email, title = "Account settings", onBack = onBack) },
  ) {
    Div(attrs = Modifier.padding(DmSpacing.space6).toAttrs()) {
      SpanText("Your account", Modifier.roleH2().displayBlock().margin(bottom = DmSpacing.space2))
      SpanText(
        "The details tied to your Draft Machine login.",
        Modifier.roleBody().color(DmColor.textSecondary).displayBlock().margin(bottom = DmSpacing.space6),
      )

      if (saved) {
        Toast(
          title = "Username saved",
          message = "Community pools now show @${user.username}.",
          tone = ToastTone.Verde,
          onClose = { saved = false },
          modifier = Modifier.margin(bottom = DmSpacing.space4),
        )
      }

      Card(variant = CardVariant.Raised, riveted = true) {
        Input(
          label = "Username",
          icon = "at",
          hint = "3–30 letters, numbers, underscores, or hyphens",
          error = usernameError,
          value = username,
          onValueChange = {
            username = it
            usernameError = null
            saved = false
          },
          onEnter = ::saveUsername,
        )
        Div(
          attrs = Modifier.displayFlex().justifyContent(JustifyContent.FlexEnd)
            .margin(top = DmSpacing.space3).toAttrs()
        ) {
          Button(
            label = "Save changes",
            icon = "floppy-disk",
            loading = saving,
            disabled = username.trim().lowercase() == user.username,
            onClick = ::saveUsername,
          )
        }
        Div(attrs = Modifier.height(1.px).backgroundColor(DmColor.border).margin(topBottom = DmSpacing.space5).toAttrs()) {}
        AccountField("Email address", user.email)
        Div(attrs = Modifier.height(1.px).backgroundColor(DmColor.border).margin(topBottom = DmSpacing.space5).toAttrs()) {}
        AccountField("Account ID", user.id)
      }

      SpanText(
        "Browser alerts",
        Modifier.roleH2().displayBlock().margin(top = DmSpacing.space8, bottom = DmSpacing.space2),
      )
      SpanText(
        "On-the-clock pushes land on the browsers you've connected.",
        Modifier.roleBody().color(DmColor.textSecondary).displayBlock().margin(bottom = DmSpacing.space4),
      )
      Card(variant = CardVariant.Panel) {
        Div(
          attrs = Modifier.displayFlex().alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.SpaceBetween).gap(DmSpacing.space4).toAttrs()
        ) {
          SpanText(
            "Check the wiring and send a test signal.",
            Modifier.roleBody().fontSize(DmType.textSm).color(DmColor.textSecondary),
          )
          Button(
            label = "Verify",
            icon = "bell-ringing",
            variant = ButtonVariant.Secondary,
            onClick = onVerifyAlerts,
          )
        }
      }

      Div(
        attrs = Modifier.displayFlex().alignItems(AlignItems.Center)
          .justifyContent(JustifyContent.SpaceBetween).gap(DmSpacing.space4)
          .margin(top = DmSpacing.space8).toAttrs()
      ) {
        SpanText("Finished for now?", Modifier.roleBody().color(DmColor.textSecondary))
        Button(
          label = "Log out",
          icon = "sign-out",
          variant = ButtonVariant.Danger,
          onClick = onLogout,
        )
      }
    }
  }
}

@Composable
private fun AccountField(label: String, value: String) {
  Div(attrs = Modifier.toAttrs()) {
    SpanText(label, Modifier.dmStamp().displayBlock().margin(bottom = DmSpacing.space2))
    SpanText(value, Modifier.roleMono().color(DmColor.text).displayBlock())
  }
}
